import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import Close from 'assets/close.svg';
import ModalPortal from './ModalPortal';

// Polyfill Promise.withResolvers for pdfjs compatibility
if (typeof window !== 'undefined' && typeof Promise.withResolvers === 'undefined') {
  Promise.withResolvers = function () {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

const PdfViewerModal = ({ isOpen, onClose, fileUrl, title }) => {
  const { t } = useTranslation('common');
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const pdfDocRef = useRef(null);
  const renderTaskRef = useRef(null);

  // Close & shortcut handler
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        setCurrentPage((prev) => Math.max(prev - 1, 1));
      } else if (e.key === 'ArrowRight') {
        setCurrentPage((prev) => (numPages ? Math.min(prev + 1, numPages) : prev));
      }

      // Block Ctrl+S, Ctrl+P, Cmd+S, Cmd+P
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'p' || e.key === 'S' || e.key === 'P')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, numPages]);

  // Load PDF Document
  useEffect(() => {
    if (!isOpen) return;

    if (!fileUrl) {
      setLoading(false);
      setError('PDF dosyası bağlantısı bulunamadı.');
      return;
    }

    let isCancelled = false;
    setLoading(true);
    setError(null);
    setCurrentPage(1);

    const loadPdf = async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        if (typeof window !== 'undefined') {
          const version = pdfjsLib.version || '3.11.174';
          const isV4 = version.startsWith('4.');
          const workerFile = isV4 ? 'pdf.worker.min.mjs' : 'pdf.worker.min.js';
          pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/${workerFile}`;
        }

        let targetUrl = fileUrl;
        if (typeof targetUrl === 'string') {
          const mediaIndex = targetUrl.indexOf('/media/');
          if (mediaIndex !== -1) {
            targetUrl = targetUrl.substring(mediaIndex);
          }
        }

        const loadingTask = pdfjsLib.getDocument({
          url: targetUrl,
          withCredentials: false,
        });
        const doc = await loadingTask.promise;

        if (isCancelled) return;
        pdfDocRef.current = doc;
        setNumPages(doc.numPages);
        setLoading(false);
      } catch (err) {
        if (!isCancelled) {
          console.error('PDF load error:', err);
          setError(err.message || 'PDF yüklenirken bir hata oluştu.');
          setLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      isCancelled = true;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (_) {
          // ignore cancel error
        }
      }
    };
  }, [isOpen, fileUrl]);

  // Render Page to Canvas
  const renderPage = useCallback(async () => {
    if (!pdfDocRef.current || !canvasRef.current) return;

    try {
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (_) {
          // ignore cancel error
        }
      }

      const page = await pdfDocRef.current.getPage(currentPage);
      const canvas = canvasRef.current;
      if (!canvas) return;

      const context = canvas.getContext('2d');
      const viewport = page.getViewport({ scale });

      const outputScale = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;

      const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

      const renderContext = {
        canvasContext: context,
        transform,
        viewport,
      };

      const renderTask = page.render(renderContext);
      renderTaskRef.current = renderTask;
      await renderTask.promise;
    } catch (err) {
      if (err?.name !== 'RenderingCancelledException') {
        console.error('Page render error:', err);
      }
    }
  }, [currentPage, scale]);

  useEffect(() => {
    if (!loading && pdfDocRef.current) {
      renderPage();
    }
  }, [loading, currentPage, scale, renderPage]);

  // Zoom handlers
  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.25, 0.5));
  const handleFitWidth = () => {
    if (containerRef.current && pdfDocRef.current) {
      const containerWidth = containerRef.current.clientWidth - 48;
      pdfDocRef.current.getPage(currentPage).then((page) => {
        const viewport = page.getViewport({ scale: 1 });
        setScale(Math.max(containerWidth / viewport.width, 0.5));
      });
    }
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-6 select-none transition-all duration-300"
        onClick={onClose}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div
          className="relative flex flex-col w-[95vw] lg:w-[85vw] max-w-6xl h-[92vh] max-h-[950px] bg-[#1a1a1a] text-white rounded-xl shadow-2xl overflow-hidden border border-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header Bar */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 bg-[#141414] border-b border-white/10 z-10">
            <span className="font-didot text-sm sm:text-base tracking-wider text-brand-soft-white truncate max-w-[60%]">
              {title || t('general.$pdfViewer') || 'PDF Belgesi'}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 sm:p-2 rounded-full hover:bg-white/10 transition-colors text-white"
              aria-label={t('general.$close') || 'Kapat'}
            >
              <Close className="stroke-white w-5 h-5" />
            </button>
          </div>

          {/* Controls Bar (Page & Zoom controls, NO download button) */}
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-6 py-2 bg-[#1f1f1f] border-b border-white/10 text-xs sm:text-sm">
            {/* Page Navigation */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage <= 1 || loading}
                className="px-2.5 py-1 rounded bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                ‹
              </button>
              <span className="text-gray-300">
                {currentPage} / {numPages || 1}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, numPages))}
                disabled={currentPage >= numPages || loading}
                className="px-2.5 py-1 rounded bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                ›
              </button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                type="button"
                onClick={handleZoomOut}
                disabled={scale <= 0.5 || loading}
                className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 disabled:opacity-30 transition font-bold"
                title="Küçült"
              >
                −
              </button>
              <span className="w-12 text-center text-gray-300">{Math.round(scale * 100)}%</span>
              <button
                type="button"
                onClick={handleZoomIn}
                disabled={scale >= 3 || loading}
                className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 disabled:opacity-30 transition font-bold"
                title="Büyüt"
              >
                +
              </button>
              <button
                type="button"
                onClick={handleFitWidth}
                disabled={loading}
                className="hidden sm:inline-block px-2.5 py-1 rounded bg-white/10 hover:bg-white/20 transition text-xs"
              >
                Genişliğe Sığdır
              </button>
            </div>
          </div>

          {/* Viewer Content Area */}
          <div
            ref={containerRef}
            className="flex-1 w-full h-full overflow-auto bg-[#2b2b2b] flex items-center justify-center p-4 relative"
            onContextMenu={(e) => e.preventDefault()}
          >
            {loading && (
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                <span className="text-xs text-gray-400">Yükleniyor...</span>
              </div>
            )}

            {error && (
              <div className="text-center p-6 bg-red-900/30 border border-red-500/30 rounded-lg text-red-200">
                <p className="font-semibold mb-1">Belge yüklenemedi</p>
                <p className="text-xs text-red-300">{error}</p>
              </div>
            )}

            <div className={`relative ${loading || error ? 'hidden' : 'block'}`}>
              <canvas
                ref={canvasRef}
                className="shadow-2xl rounded mx-auto block bg-white"
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

PdfViewerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fileUrl: PropTypes.string,
  title: PropTypes.string,
};

export default PdfViewerModal;
